import { forwardRef } from 'react';
import LogoIcon from '../../assets/icons/logo-icon.svg';

export const logoSizes = {
  small: {
    icon: 'w-12 h-12',
    text: 'text-sm',
    gap: 'gap-1'
  },
  medium: {
    icon: 'w-16 h-16',
    text: 'text-xl',
    gap: 'gap-1'
  },
  large: {
    icon: 'w-20 h-20',
    text: 'text-2xl',
    gap: 'gap-1'
  }
} as const;

export type LogoSize = keyof typeof logoSizes;

export interface LogoProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  isClickable?: boolean;
  size?: LogoSize;
  firstText?: string;
  secondText?: string;
  showText?: boolean;
}

export const Logo = forwardRef<HTMLDivElement, LogoProps>(({
  className = '',
  isClickable = false,
  size = 'large',
  firstText = 'Data',
  secondText = 'Founder',
  showText = true,
  onClick,
  ...props
}, ref) => {
  const sizeConfig = logoSizes[size];
  
  // Moved flex classes to a wrapper div to prevent conflicts with responsive classes
  const interactiveClasses = isClickable 
    ? 'cursor-pointer hover:opacity-90 transition-opacity duration-200 focus:outline-none' 
    : '';

  return (
    <div 
      ref={ref}
      className={`${interactiveClasses} ${className}`.trim()}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      {...props}
    >
      {/* Inner wrapper to maintain flex layout */}
      <div className={`inline-flex items-center ${sizeConfig.gap}`}>
        {/* Logo icon */}
        <div 
          className={sizeConfig.icon}
          aria-hidden="true"
        >
          <img 
            src={LogoIcon} 
            alt=""
            className="w-full h-full object-contain"
            loading="eager"
            draggable={false}
          />
        </div>

        {/* Logo text */}
        {showText && (
          <div className="flex flex-col whitespace-nowrap">
            <span 
              className={`font-bold ${sizeConfig.text} text-primary`}
            >
              {firstText}
            </span>
            <span 
              className={`font-bold ${sizeConfig.text} text-black dark:text-white`}
            >
              {secondText}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

Logo.displayName = 'Logo';